/**
 * How a thing's text reads when it is taller than the reading panel (ADR-077): it scrolls inside the panel, or
 * it turns pages of whole lines. A sign's authors choose it per sign as the `layout` field of its behaviour; the
 * map server keeps it at load and names it to the page in `readingLayout` before the reply it shapes.
 */

/**
 * The layouts, in the order the editor offers them.
 */
export const READING_LAYOUTS = ["scroll", "pages"] as const;

/**
 * One layout.
 */
export type ReadingLayout = (typeof READING_LAYOUTS)[number];

/**
 * The layout of a sign saved without one, of a layout a reader does not know, and of every reply the page hears
 * no `readingLayout` for.
 */
export const DEFAULT_READING_LAYOUT: ReadingLayout = "scroll";

/**
 * A layout as a record names it: a sign's authored fields, or a `readingLayout` frame's fields. Only the exact word
 * `pages` turns pages; anything else, absent or of another type, scrolls, so a world saved before layouts and a
 * layout added after this build both read.
 *
 * @param fields The record holding `layout`.
 * @returns The layout.
 * @example
 * readingLayoutOf({ text: "Willowmere, 2 leagues west.", layout: "pages" }); // "pages"
 */
export function readingLayoutOf(fields: Readonly<Record<string, unknown>>): ReadingLayout {
  return fields["layout"] === "pages" ? "pages" : DEFAULT_READING_LAYOUT;
}
